import { createStore } from 'vuex'
const USER_STORAGE_KEY = 'usuario_cliente'
const MODULES_STORAGE_KEY = 'modulos_cliente'
const PERMISOS_STORAGE_KEY = 'permisos_cliente'
const SUBSCRIPTION_STORAGE_KEY = 'suscripcion_cliente'
const USER_STORAGE_KEY_LEGADO = 'client_user'
const MODULES_STORAGE_KEY_LEGADO = 'client_modules'
const SUBSCRIPTION_STORAGE_KEY_LEGADO = 'client_subscription'

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

const loadFromStorage = (key, fallback) => {
  if (!isBrowser()) return fallback
  const raw = window.localStorage.getItem(key)
  if (!raw) return fallback
  try {
    return JSON.parse(raw)
  } catch (error) {
    console.warn(`[store] No se pudo parsear ${key} desde localStorage`, error)
    return fallback
  }
}

const loadFromStorageMulti = (keys, fallback) => {
  for (const key of keys) {
    const value = loadFromStorage(key, null)
    if (value !== null) return value
  }
  return fallback
}

const persist = (key, value) => {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.warn(`[store] No se pudo persistir ${key} en localStorage`, error)
  }
}

const remove = (key) => {
  if (!isBrowser()) return
  window.localStorage.removeItem(key)
}

export default createStore({
  state: {
    user: loadFromStorageMulti([USER_STORAGE_KEY, USER_STORAGE_KEY_LEGADO], null),
    modules: loadFromStorageMulti([MODULES_STORAGE_KEY, MODULES_STORAGE_KEY_LEGADO], []),
    permisos: loadFromStorage(PERMISOS_STORAGE_KEY, []),
    subscription: loadFromStorageMulti([SUBSCRIPTION_STORAGE_KEY, SUBSCRIPTION_STORAGE_KEY_LEGADO], null),
    // true solo cuando ya se resolvio la carga real de permisos (cargarPermisosEfectivos).
    // Mientras sea false, el menu/router deben tratarse como "aun cargando", nunca como "sin restriccion".
    modulesLoaded: false
  },
  getters: {
    estaAutenticado: state => Boolean(state.user),
    isSuperAdmin: state => Boolean(state.user?.is_super_admin),
    currentUser: state => state.user,
    esAdministrador: state => Boolean(state.user?.es_administrador),

    // Módulos activos de la empresa (Capa 1)
    activeModules: state => (Array.isArray(state.modules) ? state.modules : []),

    /**
     * Verifica si la empresa tiene activo el módulo indicado.
     * @param {string} code  Ruta del módulo secundario o principal (ej: 'ventas/pedidos' o 'ventas')
     */
    hasModule: (state, getters) => (code) => {
      if (!code) return true
      if (getters.isSuperAdmin) return true
      const activos = getters.activeModules.map(m => String(m))
      return activos.includes(String(code))
    },

    // Permisos del rol del usuario (Capa 2)
    // Formato interno: ['ventas/pedidos.ver', 'ventas/facturacion.crear', ...]
    activePermisos: state => (Array.isArray(state.permisos) ? state.permisos : []),

    /**
     * Verifica si el usuario tiene un permiso concreto.
     * @param {string} ruta   Ruta del módulo secundario (ej: 'ventas/pedidos')
     * @param {string} accion Acción ('ver','crear','actualizar','eliminar')
     */
    puedoHacer: (state, getters) => (ruta, accion) => {
      if (!ruta || !accion) return true
      if (getters.isSuperAdmin) return true
      const clave = `${ruta}.${accion}`
      return getters.activePermisos.includes(clave)
    },

    /**
     * Alias: verifica 'ver' para una ruta.
     */
    puedoVer: (state, getters) => (ruta) => getters.puedoHacer(ruta, 'ver'),

    subscription: state => state.subscription,
    subscriptionEstado: state => state.subscription?.estado || null,
    hasActiveSubscription: state => {
      const estado = state.subscription?.estado
      return estado === 'activa' || estado === 'en_gracia'
    },
    isSubscriptionBlocking: state => {
      const estado = state.subscription?.estado
      if (!estado) return false
      return estado !== 'activa' && estado !== 'en_gracia'
    },
    isSubscriptionInGrace: state => state.subscription?.estado === 'en_gracia',

    isPlanBasico: state => {
      const nombre = state.subscription?.plan_nombre
      return typeof nombre === 'string' && nombre.trim().toUpperCase() === 'PLAN BASICO'
    }
  },
  mutations: {
    setUser(state, user) {
      state.user = user || null
      if (user) {
        persist(USER_STORAGE_KEY, state.user)
      } else {
        remove(USER_STORAGE_KEY)
      }
    },
    setModules(state, modules) {
      state.modules = Array.isArray(modules) ? modules : []
      persist(MODULES_STORAGE_KEY, state.modules)
    },
    setPermisos(state, permisos) {
      state.permisos = Array.isArray(permisos) ? permisos : []
      persist(PERMISOS_STORAGE_KEY, state.permisos)
    },
    setModulesLoaded(state, loaded) {
      state.modulesLoaded = !!loaded
    },
    setSubscription(state, subscription) {
      state.subscription = subscription || null
      if (subscription) {
        persist(SUBSCRIPTION_STORAGE_KEY, state.subscription)
      } else {
        remove(SUBSCRIPTION_STORAGE_KEY)
      }
    },
    clearAuth(state) {
      state.user = null
      state.modules = []
      state.permisos = []
      state.subscription = null
      state.modulesLoaded = false
      remove(USER_STORAGE_KEY)
      remove(MODULES_STORAGE_KEY)
      remove(PERMISOS_STORAGE_KEY)
      remove(SUBSCRIPTION_STORAGE_KEY)
      remove(USER_STORAGE_KEY_LEGADO)
      remove(MODULES_STORAGE_KEY_LEGADO)
      remove(SUBSCRIPTION_STORAGE_KEY_LEGADO)
    }
  },
  actions: {
    hydrateFromStorage({ commit }) {
      commit('setUser', loadFromStorageMulti([USER_STORAGE_KEY, USER_STORAGE_KEY_LEGADO], null))
      commit('setModules', loadFromStorageMulti([MODULES_STORAGE_KEY, MODULES_STORAGE_KEY_LEGADO], []))
      commit('setPermisos', loadFromStorage(PERMISOS_STORAGE_KEY, []))
      commit('setSubscription', loadFromStorageMulti([SUBSCRIPTION_STORAGE_KEY, SUBSCRIPTION_STORAGE_KEY_LEGADO], null))
    },

    /**
     * Carga los permisos efectivos del usuario autenticado desde
     * /control-acceso/mis-permisos y los persiste en el store.
     * Llamar tras login y al iniciar el layout autenticado.
     */
    async cargarPermisosEfectivos({ commit }) {
      const { default: controlAcceso } = await import('@/services/controlAccesoService')
      try {
        const res = await controlAcceso.misPermisos()
        const data = res?.data || {}
        const submodulos = Array.isArray(data.submodulos) ? data.submodulos : []
        const acciones = data.acciones && typeof data.acciones === 'object' ? Object.keys(data.acciones) : []
        commit('setModules', submodulos)
        commit('setPermisos', acciones)
        commit('setSubscription', data.suscripcion || null)
        if (data.es_administrador !== undefined) {
          const user = loadFromStorage(USER_STORAGE_KEY, null)
          if (user) {
            user.es_administrador = !!data.es_administrador
            commit('setUser', user)
          }
        }
        commit('setModulesLoaded', true)
        return data
      } catch (e) {
        console.error('[store] No se pudieron cargar permisos efectivos', e)
        // Ante un fallo real de carga, no se debe abrir el menu completo:
        // se marca como cargado (con lo que ya haya en el store) y se
        // filtra igual que un usuario sin permisos.
        commit('setModulesLoaded', true)
        return null
      }
    }
  }
})

